import React from "react";
import PropTypes from "prop-types";
import { Select, Spin, message } from "antd";
import CaseLeegalityService from "services/case-leegality-agreement-service";
import { Icon, ICONS, StatusBadge, Pill } from "./ui";
import { checklistTone, checklistLabel, retriggerDraftFor, rowFitsRetriggerReason } from "./data-utils";
import { CHECKLIST_STATE } from "./constants";

/**
 * Right-hand drawer listing every PS Kit checklist row — form fields first,
 * then documents — with the source of truth each value is validated against.
 *
 * Rows are live: clicking one jumps to the section that owns it, and "Flag
 * wrong" collects it into a re-trigger request. The re-trigger reasons are
 * fixed dropdowns served by GET /case/v2/leegality/retrigger/; submitting only
 * saves them in CaseLegalityStatusLogs (remarks + json_data) - the kit itself
 * is not cancelled or re-triggered from here.
 */
const LEGEND = [
    { tone: "ok", label: "Complete" },
    { tone: "bad", label: "Wrong" },
    { tone: "warn", label: "Missing" }
];

const { Option, OptGroup } = Select;

// One dropdown carries both levels of the backend master: "<category>|<reason>".
const reasonValue = draft => (draft.category && draft.reason ? `${draft.category}|${draft.reason}` : undefined);

let draftSeq = 0;
const withKey = draft => ({ key: `draft-${(draftSeq += 1)}`, ...draft });

/**
 * One checklist line: what is captured (not just where it should come from)
 * and what can be done about it right here.
 *   form field -> read only; flag it wrong if it needs correcting
 *   document   -> Request from the customer, or open Documents
 */
class ChecklistRow extends React.Component {
    render() {
        const { row, flagged, canFlag, canEdit, onToggleFlag, onGo, onRequestDoc } = this.props;
        const isForm = !!row.field;
        const isDoc = !isForm;
        let valueText = row.display;
        if (isDoc) valueText = row.status === CHECKLIST_STATE.COMPLETE ? "On file" : row.status === CHECKLIST_STATE.WRONG ? "Rejected on review" : null;
        return (
            <div className={"ops-ws__ck-row"}>
                <StatusBadge tone={checklistTone(row.status)} />
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div className={"ops-ws__ck-title"}>
                        <span className={"ops-ws__ck-name"}>{row.name}</span>
                        {!row.mandatory ? <span className={"ops-ws__ck-optional"}>{"Optional"}</span> : null}
                    </div>
                    <div className={"ops-ws__ck-value" + (valueText ? "" : " is-empty")} title={valueText || ""}>
                        {valueText || (isDoc ? "Not on file" : "Not captured")}
                    </div>
                    <div className={"ops-ws__ck-source"}>
                        {isDoc ? row.source : `Source: ${row.source}`}
                    </div>
                    {row.status === CHECKLIST_STATE.WRONG && row.reason && isForm ? (
                        <div className={"ops-ws__ck-reason"}>{row.reason}</div>
                    ) : null}
                </div>
                <div className={"ops-ws__ck-actions"}>
                    <Pill tone={checklistTone(row.status)}>{checklistLabel(row.status)}</Pill>
                    <div className={"ops-ws__ck-buttons"}>
                        {isDoc && row.status !== CHECKLIST_STATE.COMPLETE && canEdit && onRequestDoc ? (
                            <button type={"button"} className={"ops-ws__ck-flag"} onClick={() => onRequestDoc(row)}>
                                {"Request"}
                            </button>
                        ) : null}
                        {isDoc ? (
                            <button type={"button"} className={"ops-ws__ck-flag"} onClick={() => onGo(row)}>
                                {row.status === CHECKLIST_STATE.COMPLETE ? "View" : "Upload"}
                            </button>
                        ) : null}
                        {canFlag ? (
                            <button
                                type={"button"}
                                className={"ops-ws__ck-flag" + (flagged.has(row.id) ? " ops-ws__ck-flag--on" : "")}
                                onClick={() => onToggleFlag(row)}
                            >
                                {flagged.has(row.id) ? "Flagged" : "Flag wrong"}
                            </button>
                        ) : null}
                    </div>
                </div>
            </div>
        );
    }
}

ChecklistRow.propTypes = {
    row: PropTypes.object.isRequired,
    flagged: PropTypes.object,
    canFlag: PropTypes.bool,
    canEdit: PropTypes.bool,
    onToggleFlag: PropTypes.func,
    onGo: PropTypes.func,
    onRequestDoc: PropTypes.func
};

const Rows = ({ rows, ...rest }) => rows.map(row => <ChecklistRow key={row.id} row={row} {...rest} />);

Rows.propTypes = { rows: PropTypes.array };

/** Form rows under one header per applicant, instead of the name on every row. */
const ApplicantGroups = ({ rows, ...rest }) => {
    const order = [];
    const byApplicant = {};
    rows.forEach(row => {
        const key = row.applicantId || row.applicant || "—";
        if (!byApplicant[key]) {
            byApplicant[key] = { name: row.applicant || "Applicant", rows: [] };
            order.push(key);
        }
        byApplicant[key].rows.push(row);
    });
    return order.map(key => (
        <div key={key}>
            <div className={"ops-ws__ck-group"}>
                <Icon d={ICONS.person} size={13} />
                <span>{byApplicant[key].name}</span>
                <span className={"ops-ws__ck-group-count"}>{clearCount(byApplicant[key].rows)}</span>
            </div>
            <Rows rows={byApplicant[key].rows} {...rest} />
        </div>
    ));
};

ApplicantGroups.propTypes = { rows: PropTypes.array };

const clearCount = rows =>
    `${rows.filter(r => r.status === CHECKLIST_STATE.COMPLETE).length} of ${rows.length} clear`;

// service-instance rejects with the response body itself; tolerate a raw
// axios error too.
const errorText = (error, fallback) => {
    const data = (error && error.response && error.response.data) || error;
    const err = data && data.error;
    if (Array.isArray(err) && err.length) return String(err[0]);
    if (typeof err === "string" && err) return err;
    return fallback;
};

class ChecklistDrawer extends React.Component {
    state = {
        master: null,
        loadingMaster: false,
        loadError: null,
        drafts: [],
        submitting: false
    };

    componentDidMount() {
        if (this.props.open) this.loadMaster();
    }

    componentDidUpdate(prevProps) {
        if (this.props.open && !prevProps.open) this.loadMaster();
        if (this.props.caseId !== prevProps.caseId) this.setState({ master: null, drafts: [] });
    }

    loadMaster() {
        const { caseId } = this.props;
        if (!caseId) return;
        this.setState({ loadingMaster: true, loadError: null });
        CaseLeegalityService.getPsKitRetriggerReasons(caseId)
            .then(({ data }) => this.setState({ master: data.data, loadingMaster: false }))
            .catch(error => {
                // service-instance rejects a 404 with the response itself.
                const notDeployed = error && error.status === 404;
                this.setState({
                    loadingMaster: false,
                    loadError: notDeployed
                        ? "The re-trigger service is not available on this server yet (case/v2/leegality/retrigger/ returned 404)."
                        : errorText(error, "Could not load the re-trigger reasons.")
                });
            });
    }

    categories() {
        return (this.state.master && this.state.master.reasons) || [];
    }

    category(key) {
        return this.categories().find(c => c.key === key);
    }

    canRetrigger() {
        return !!(this.props.canRetrigger && this.state.master && this.state.master.can_retrigger);
    }

    toggleFlag = row => {
        const exists = this.state.drafts.some(d => d.rowId === row.id);
        this.setState({
            drafts: exists
                ? this.state.drafts.filter(d => d.rowId !== row.id)
                : this.state.drafts.concat(withKey(retriggerDraftFor(row)))
        });
    };

    /** One click for every row the checklist already marks Wrong. */
    flagAllWrong = () => {
        const flagged = new Set(this.state.drafts.map(d => d.rowId));
        const fresh = this.props.formRows
            .concat(this.props.docRows)
            .filter(row => row.status === CHECKLIST_STATE.WRONG && !flagged.has(row.id))
            .map(row => withKey(retriggerDraftFor(row)));
        this.setState({ drafts: this.state.drafts.concat(fresh) });
    };

    wrongCount() {
        return this.props.formRows
            .concat(this.props.docRows)
            .filter(row => row.status === CHECKLIST_STATE.WRONG).length;
    }

    addDraft = () =>
        this.setState({
            drafts: this.state.drafts.concat(
                withKey({ rowId: null, category: undefined, reason: undefined, item: "", applicant: "" })
            )
        });

    removeDraft = key => this.setState({ drafts: this.state.drafts.filter(d => d.key !== key) });

    patchDraft = (key, patch) =>
        this.setState({
            drafts: this.state.drafts.map(d => (d.key === key ? { ...d, ...patch } : d))
        });

    onReason = (draft, value) => {
        const [category, reason] = (value || "").split("|");
        const patch = { category, reason };
        // Keep what the line points at while it still fits the new reason.
        const row = this.rowById(draft.rowId);
        const target = (this.category(category) || {}).target;
        const keepRow = row && target !== "applicant" && rowFitsRetriggerReason(row, category, reason);
        const keepApplicant = target === "applicant" || (target === "field" && keepRow);
        this.patchDraft(draft.key, {
            ...patch,
            rowId: keepRow ? draft.rowId : null,
            item: keepRow ? draft.item : "",
            applicant: keepApplicant ? draft.applicant : ""
        });
    };

    rowById(id) {
        if (!id) return null;
        return this.props.formRows.concat(this.props.docRows).find(row => row.id === id) || null;
    }

    /**
     * "Applies to" options, driven by the category's target in the master and
     * narrowed to the rows that fit the picked reason.
     */
    targetOptions(target, draft) {
        const { formRows, docRows } = this.props;
        const fits = row => rowFitsRetriggerReason(row, draft.category, draft.reason);
        if (target === "field") {
            return formRows.filter(fits).map(row => ({
                value: row.id,
                label: row.applicant ? `${row.name} · ${row.applicant}` : row.name,
                patch: { rowId: row.id, item: row.name, applicant: row.applicant || "" }
            }));
        }
        if (target === "document") {
            return docRows.filter(fits).map(row => ({
                value: row.id,
                label: `${row.name} · ${row.source}`,
                patch: { rowId: row.id, item: row.name, applicant: "" }
            }));
        }
        if (target === "applicant") {
            const names = Array.from(new Set(formRows.map(row => row.applicant).filter(Boolean)));
            return names.map(name => ({
                value: `applicant:${name}`,
                label: name,
                patch: { rowId: null, item: "", applicant: name }
            }));
        }
        return [];
    }

    targetValue(draft, target) {
        if (target === "applicant") return draft.applicant ? `applicant:${draft.applicant}` : undefined;
        return draft.rowId || undefined;
    }

    submit = () => {
        const incomplete = this.state.drafts.some(d => !d.category || !d.reason);
        if (!this.state.drafts.length || incomplete) {
            message.error("Pick a reason for every line");
            return;
        }
        const reasons = this.state.drafts.map(d => ({
            category: d.category,
            reason: d.reason,
            item: d.item || "",
            applicant: d.applicant || ""
        }));
        this.setState({ submitting: true });
        CaseLeegalityService.retriggerPsKit(this.props.caseId, reasons)
            .then(({ data }) => {
                message.success((data && data.data && data.data.message) || "Re-trigger reasons saved");
                this.setState({ submitting: false, drafts: [] });
                this.props.onRetriggered();
            })
            .catch(error => {
                message.error(errorText(error, "Could not save the re-trigger reasons"));
                this.setState({ submitting: false });
                this.loadMaster();
            });
    };

    renderDraft(draft) {
        const category = this.category(draft.category);
        const target = category ? category.target : null;
        const options = this.targetOptions(target, draft);
        return (
            <div key={draft.key} className={"ops-ws__rt-line"}>
                <Select
                    size={"small"}
                    showSearch
                    placeholder={"Search or pick a reason"}
                    value={reasonValue(draft)}
                    onChange={value => this.onReason(draft, value)}
                    filterOption={(input, option) =>
                        String(option.props.title || "")
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                    }
                    className={"ops-ws__rt-select ops-ws__rt-select--wide"}
                    dropdownMatchSelectWidth={false}
                >
                    {this.categories().map(c => (
                        <OptGroup key={c.key} label={c.label}>
                            {c.reasons.map(r => (
                                <Option key={`${c.key}|${r.key}`} value={`${c.key}|${r.key}`} title={`${c.label} ${r.label}`}>
                                    {r.label}
                                </Option>
                            ))}
                        </OptGroup>
                    ))}
                </Select>
                <Select
                    size={"small"}
                    showSearch
                    allowClear
                    optionFilterProp={"children"}
                    placeholder={target ? "Applies to (optional)" : category ? "Not applicable" : "Pick a reason first"}
                    value={this.targetValue(draft, target)}
                    disabled={!target}
                    onChange={value => {
                        const picked = options.find(o => o.value === value);
                        this.patchDraft(
                            draft.key,
                            picked ? picked.patch : { rowId: null, item: "", applicant: "" }
                        );
                    }}
                    className={"ops-ws__rt-select ops-ws__rt-select--wide"}
                    dropdownMatchSelectWidth={false}
                >
                    {options.map(o => (
                        <Option key={o.value} value={o.value}>
                            {o.label}
                        </Option>
                    ))}
                </Select>
                <button
                    type={"button"}
                    className={"ops-ws__close ops-ws__rt-remove"}
                    onClick={() => this.removeDraft(draft.key)}
                    aria-label={"Remove reason"}
                >
                    <Icon d={ICONS.cross} size={13} width={2.2} />
                </button>
            </div>
        );
    }

    renderRetrigger() {
        const { master, loadingMaster, loadError, drafts, submitting } = this.state;
        // Never drop the panel silently: say why re-trigger is not on offer.
        if (!this.props.canRetrigger) {
            return (
                <div className={"ops-ws__rt"}>
                    <div className={"ops-ws__summary-note"}>
                        PS Kit re-trigger reasons need the post-sanction group.
                    </div>
                </div>
            );
        }
        if (loadingMaster && !master) {
            return (
                <div className={"ops-ws__rt"}>
                    <Spin size={"small"} />
                </div>
            );
        }
        if (!master) {
            return (
                <div className={"ops-ws__rt"} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div className={"ops-ws__summary-note"} style={{ flex: 1 }}>
                        {loadError || "Re-trigger reasons are not loaded."}
                    </div>
                    <button type={"button"} className={"ops-ws__btn"} onClick={() => this.loadMaster()}>
                        Retry
                    </button>
                </div>
            );
        }
        if (!master.can_retrigger) {
            return (
                <div className={"ops-ws__rt"}>
                    <div className={"ops-ws__summary-note"}>
                        Re-trigger unavailable{master.status ? ` (${master.status})` : ""}: {master.message}
                    </div>
                </div>
            );
        }
        return (
            <div className={"ops-ws__rt"}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 800 }}>PS Kit re-trigger reasons</div>
                        <div className={"ops-ws__summary-note"}>
                            Flag what is wrong above or add a reason. The reasons are saved to the
                            PS Kit log; the kit ({master.status}) is not changed from here.
                        </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, flex: "none" }}>
                        {this.wrongCount() ? (
                            <button type={"button"} className={"ops-ws__btn"} onClick={this.flagAllWrong}>
                                Flag all wrong ({this.wrongCount()})
                            </button>
                        ) : null}
                        <button type={"button"} className={"ops-ws__btn"} onClick={this.addDraft}>
                            + Add reason
                        </button>
                    </div>
                </div>
                {drafts.length ? (
                    <div className={"ops-ws__rt-lines"}>{drafts.map(d => this.renderDraft(d))}</div>
                ) : null}
                <button
                    type={"button"}
                    className={"ops-ws__btn ops-ws__btn--primary ops-ws__btn--block"}
                    disabled={!drafts.length || submitting}
                    onClick={this.submit}
                    style={{ marginTop: 10 }}
                >
                    {submitting
                        ? "Saving…"
                        : `Save re-trigger reasons${drafts.length ? ` (${drafts.length})` : ""}`}
                </button>
            </div>
        );
    }

    render() {
        const { open, onClose, formRows, docRows, onGoToRow } = this.props;
        if (!open) return null;
        const flagged = new Set(this.state.drafts.map(d => d.rowId).filter(Boolean));
        const rowProps = {
            flagged,
            canFlag: this.canRetrigger(),
            canEdit: this.props.canEdit,
            onToggleFlag: this.toggleFlag,
            onGo: onGoToRow,
            onRequestDoc: this.props.onRequestDoc
        };
        return (
            <React.Fragment>
                <div className={"ops-ws__scrim"} onClick={onClose} />
                <aside className={"ops-ws__drawer"} role={"dialog"} aria-label={"PS Kit checklist"}>
                    <div className={"ops-ws__drawer-head"}>
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: "-0.01em" }}>
                                PS Kit checklist
                            </div>
                            <div className={"ops-ws__summary-note"}>
                                What is captured for each form field and document, checked
                                against its source. Fix it right here or flag it for re-trigger.
                            </div>
                        </div>
                        <button type={"button"} className={"ops-ws__close"} onClick={onClose}>
                            <Icon d={ICONS.cross} size={16} width={2.2} />
                        </button>
                    </div>

                    <div className={"ops-ws__drawer-legend"}>
                        {LEGEND.map(item => (
                            <span key={item.tone} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <StatusBadge tone={item.tone} small />
                                <span style={{ fontSize: 11, fontWeight: 700, color: "#475467" }}>
                                    {item.label}
                                </span>
                            </span>
                        ))}
                    </div>

                    <div className={"ops-ws__drawer-body"}>
                        <div className={"ops-ws__drawer-section"}>
                            <span className={"ops-ws__eyebrow ops-ws__eyebrow--primary"}>
                                Form checklist
                            </span>
                            <span style={{ fontSize: 11, fontWeight: 700, color: "#98a2b3" }}>
                                {clearCount(formRows)}
                            </span>
                        </div>
                        <div style={{ borderTop: "1px solid #eef0f3", borderBottom: "1px solid #eef0f3" }}>
                            <ApplicantGroups rows={formRows} {...rowProps} />
                        </div>

                        <div className={"ops-ws__drawer-section"} style={{ paddingTop: 18 }}>
                            <span className={"ops-ws__eyebrow ops-ws__eyebrow--primary"}>
                                Document checklist
                            </span>
                            <span style={{ fontSize: 11, fontWeight: 700, color: "#98a2b3" }}>
                                {clearCount(docRows)}
                            </span>
                        </div>
                        <div style={{ borderTop: "1px solid #eef0f3" }}>
                            <Rows rows={docRows} {...rowProps} />
                        </div>
                        <div style={{ height: 24 }} />
                    </div>

                    {this.renderRetrigger()}
                </aside>
            </React.Fragment>
        );
    }
}

ChecklistDrawer.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    formRows: PropTypes.array.isRequired,
    docRows: PropTypes.array.isRequired,
    caseId: PropTypes.number,
    canRetrigger: PropTypes.bool,
    onGoToRow: PropTypes.func.isRequired,
    onRetriggered: PropTypes.func.isRequired,
    canEdit: PropTypes.bool,
    onRequestDoc: PropTypes.func
};

ChecklistDrawer.defaultProps = {
    canRetrigger: false,
    canEdit: false,
    onRequestDoc: null
};

export default ChecklistDrawer;
